// This file is auto-generated by @hey-api/openapi-ts

import { client } from './Client.gen';
import type { EshopOAuthControllerLinkData } from './EshopOAuthControllerLinkData.gen';
import type { EshopOAuthControllerLinkErrors } from './EshopOAuthControllerLinkErrors.gen';
import type { EshopOAuthControllerLinkResponses } from './EshopOAuthControllerLinkResponses.gen';
import type { EshopOAuthControllerLoginData } from './EshopOAuthControllerLoginData.gen';
import type { EshopOAuthControllerLoginErrors } from './EshopOAuthControllerLoginErrors.gen';
import type { EshopOAuthControllerLoginResponses } from './EshopOAuthControllerLoginResponses.gen';
import type { EshopOAuthControllerRegisterData } from './EshopOAuthControllerRegisterData.gen';
import type { EshopOAuthControllerRegisterErrors } from './EshopOAuthControllerRegisterErrors.gen';
import type { EshopOAuthControllerRegisterResponses } from './EshopOAuthControllerRegisterResponses.gen';
import type { Options } from './Options.gen';

export class EshopOAuthController {
    /**
     * Authenticate a customer using google id token
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_CREDENTIALS** - Username, cardNumber or password is invalid. (also can mean: card is blocked, expired or not valid yet, user is disabled).<br>
     * + **INVALID_OAUTH_TOKEN** - Provided OAuth token is not valid<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.<br>
     * + **CUSTOMER_PARENT_NOT_VERIFIED** - Customer parent email is not verified.<br>
     * + **CUSTOMER_CARD_ALREADY_EXISTS** - Customer card already exists.<br>
     * + **REGULAR_CUSTOMER_PROFILE_NOT_FOUND** - Regular customer profile does not exist.<br>
     * + **ACCOUNT_RESTRICTED_TO_MOBILE_ACCESS** - This account can only be accessed via the mobile app.
     */
    public static login<ThrowOnError extends boolean = false>(options: Options<EshopOAuthControllerLoginData, ThrowOnError>) {
        return (options.client ?? client).post<EshopOAuthControllerLoginResponses, EshopOAuthControllerLoginErrors, ThrowOnError>({
            url: '/api/eshop/oauth/login',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Use OAuth email to create a new eshop account
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_OAUTH_TOKEN** - Provided OAuth token is not valid<br>
     * + **OAUTH_EMAIL_MISSING_IN_JWT_TOKEN** - OAuth email is missing in JWT token.<br>
     * + **OAUTH_LINK_EMAIL_MISMATCH** - Link email send in request payload does not match with email in JWT token.<br>
     * + **TERMS_AND_CONDITIONS_NOT_AGREED_TO** - To register, the terms and conditions must be agreed to.<br>
     * + **USERNAME_OR_OAUTH_EMAIL_IS_REQUIRED** - To register, username or OAuth email is required.<br>
     * + **REGISTRATION_UNDER_15_MISSING_PROPERTIES** - Registration under 15 years request is missing "isUnder15Years" property.<br>
     * + **REGISTRATION_UNDER_15_NOT_ALLOWED** - Registration under 15 years is not allowed in system configuration.<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **USERNAME_AND_PARENT_EMAIL_CAN_NOT_BE_SAME** - Username and parent email can not be same.<br>
     * + **PARENT_FOR_OLDER_THAN_15_YEARS_NOT_ALLOWED** - Parent email can not be set for users older than 15 years.<br>
     * + **CUSTOMER_CARD_ALREADY_EXISTS** - Customer card already exists.<br>
     * + **REGULAR_CUSTOMER_PROFILE_NOT_FOUND** - Regular customer profile does not exist.
     */
    public static register<ThrowOnError extends boolean = false>(options: Options<EshopOAuthControllerRegisterData, ThrowOnError>) {
        return (options.client ?? client).post<EshopOAuthControllerRegisterResponses, EshopOAuthControllerRegisterErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/oauth/register',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Use google email to link existing eshop account and update to google email
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_CREDENTIALS** - Username, cardNumber or password is invalid. (also can mean: card is blocked, expired or not valid yet, user is disabled).<br>
     * + **OAUTH_EMAIL_MISSING_IN_JWT_TOKEN** - OAuth email is missing in JWT token.<br>
     * + **OAUTH_LINK_EMAIL_MISMATCH** - Link email send in request payload does not match with email in JWT token.<br>
     * + **OAUTH_LINK_ACCOUNT_FAIL** - OAuth link account failed. Customer account was't linked. Account with oAuth email may be already registered.<br>
     * + **CUSTOMER_PARENT_NOT_VERIFIED** - Customer parent email is not verified.<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.
     */
    public static link<ThrowOnError extends boolean = false>(options: Options<EshopOAuthControllerLinkData, ThrowOnError>) {
        return (options.client ?? client).post<EshopOAuthControllerLinkResponses, EshopOAuthControllerLinkErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/oauth/link',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
}
